/**
 * Download an image in Cordova (Android/iOS) or fall back to browser file-saver.
 * WebViews often block <a download> / saveAs; we fetch, write via cordova-plugin-file, then open the file.
 */

function sanitizeFileBase(name) {
  return String(name || 'image').replace(/[^a-z0-9._-]+/gi, '_').slice(0, 80) || 'image';
}

function cordovaReady() {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || !window.cordova) {
      reject(new Error('not-cordova'));
      return;
    }
    const done = () => {
      if (window.cordova.file) resolve();
      else reject(new Error('cordova.file missing'));
    };
    if (window.cordova.file) {
      done();
      return;
    }
    const t = setTimeout(() => reject(new Error('deviceready timeout')), 15000);
    document.addEventListener(
      'deviceready',
      () => {
        clearTimeout(t);
        done();
      },
      { once: true }
    );
  });
}

function saveBlobWithCordovaFile(blob, fileName) {
  return new Promise((resolve, reject) => {
    const win = window;
    const baseDir =
      win.cordova.platformId === 'ios'
        ? win.cordova.file.documentsDirectory
        : win.cordova.file.externalDataDirectory ||
          win.cordova.file.externalCacheDirectory ||
          win.cordova.file.dataDirectory;

    if (!baseDir) {
      reject(new Error('no cordova file directory'));
      return;
    }

    win.resolveLocalFileSystemURL(
      baseDir,
      (dirEntry) => {
        dirEntry.getFile(
          fileName,
          { create: true, exclusive: false },
          (fileEntry) => {
            fileEntry.createWriter(
              (writer) => {
                writer.onwriteend = () => resolve(fileEntry);
                writer.onerror = () => reject(writer.error || new Error('write failed'));
                writer.write(blob);
              },
              reject
            );
          },
          reject
        );
      },
      reject
    );
  });
}

function openLocalFile(fileEntry) {
  const url = fileEntry.nativeURL || (typeof fileEntry.toURL === 'function' && fileEntry.toURL());
  if (url && window.cordova && window.cordova.InAppBrowser) {
    window.cordova.InAppBrowser.open(url, '_system');
  }
}

function openRemoteFallback(imageUrl) {
  if (window.cordova && window.cordova.InAppBrowser) {
    window.cordova.InAppBrowser.open(imageUrl, '_system');
  }
}

function fetchBlobWithXhr(imageUrl) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', imageUrl, true);
    xhr.responseType = 'blob';
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300 && xhr.response) {
        resolve(xhr.response);
      } else {
        reject(new Error(`XHR download failed: ${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new Error('XHR network error'));
    xhr.send();
  });
}

async function getImageBlob(imageUrl) {
  if (!imageUrl) throw new Error('Invalid image URL');

  if (typeof fetch === 'function') {
    try {
      const res = await fetch(imageUrl, { mode: 'cors', credentials: 'omit' });
      if (res && typeof res.blob === 'function' && (typeof res.ok !== 'boolean' || res.ok)) {
        return await res.blob();
      }
    } catch (e) {
      // Fall through to XHR for older/non-standard WebViews.
    }
  }

  return fetchBlobWithXhr(imageUrl);
}

/**
 * @param {string} imageUrl
 * @param {string} filenameBase without extension
 * @param {(blob: Blob, name: string) => void} saveAs file-saver saveAs when not in Cordova
 */
export async function downloadImageUniversal(imageUrl, filenameBase, saveAs) {
  const fileName = `${sanitizeFileBase(filenameBase)}_${Date.now()}.jpg`;

  if (typeof window !== 'undefined' && window.cordova) {
    // Android: opening local file:// after write is unreliable across devices/webviews.
    // Let the system browser handle remote URL download directly.
    if (window.cordova.platformId === 'android') {
      openRemoteFallback(imageUrl);
      return;
    }

    const blob = await getImageBlob(imageUrl);
    try {
      await cordovaReady();
      const fileEntry = await saveBlobWithCordovaFile(blob, fileName);
      openLocalFile(fileEntry);
    } catch (e) {
      console.warn('[downloadImageUniversal] Cordova file save failed', e);
      // Do not use blob/object URLs in Cordova fallback (can be blocked on insecure origins).
      // Open the remote HTTPS URL in system browser as last resort.
      openRemoteFallback(imageUrl);
    }
    return;
  }

  const blob = await getImageBlob(imageUrl);
  saveAs(blob, fileName);
}
